import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  DocumentArrowDownIcon,
  FunnelIcon,
  BuildingLibraryIcon,
  CurrencyDollarIcon,
  ClockIcon,
  ShieldExclamationIcon,
  HomeIcon
} from '@heroicons/react/24/outline';
import { useAppData } from '../../contexts/AppDataContext';
import { useCompanySettings } from '../../contexts/CompanySettingsContext';
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const StorageCostsAnalysis = () => {
  const navigate = useNavigate();
  const { products, warehouses, purchases, stockAdjustments } = useAppData();
  const { companySettings } = useCompanySettings();
  
  const [timeRange, setTimeRange] = useState('month');
  const [warehouseFilter, setWarehouseFilter] = useState('all');
  const [isCalculating, setIsCalculating] = useState(false);
  
  // Données de coûts réelles
  const [storageCostsData, setStorageCostsData] = useState({
    totalCost: 0,
    costByCategory: [],
    costByWarehouse: [],
    topExpensiveItems: [],
    costDetails: []
  });

  // Périodes disponibles
  const timeRanges = [
    { id: 'month', name: 'Ce mois' },
    { id: 'quarter', name: 'Ce trimestre' },
    { id: 'year', name: 'Cette année' },
  ];

  // Entrepôts disponibles - utiliser uniquement les données réelles
  const warehouseOptions = [
      { id: 'all', name: 'Tous les entrepôts' },
    ...(warehouses && warehouses.length > 0 
      ? warehouses.map(w => ({ id: w.id, name: w.name }))
      : [])
  ];

  // Utiliser uniquement les données réelles - pas de catégories théoriques

  // Extraire les catégories réelles des produits
  const productCategories = useMemo(() => {
    if (!products || products.length === 0) return [];
    
    const categories = {};
    products.forEach(product => {
      if (product.category) {
        categories[product.category] = product.category;
      }
    });
    
    return Object.keys(categories).map(id => ({
      id,
      name: id
    }));
  }, [products]);

  // Calcul des données de coûts de stockage - mis à jour à chaque changement de filtre
  useEffect(() => {
    calculateStorageCosts();
  }, [warehouseFilter, timeRange, products, warehouses, stockAdjustments, purchases]);

  const calculateStorageCosts = useCallback(() => {
    setIsCalculating(true);
    
    try {
      console.log('🔄 Calculating storage costs with real-time data...');
      console.log('📊 Data available:', {
        productsCount: products?.length || 0,
        warehousesCount: warehouses?.length || 0,
        stockAdjustmentsCount: stockAdjustments?.length || 0,
        purchasesCount: purchases?.length || 0
      });
      
      // 1. Coût total - selon la période et le stock actuel
      // Utiliser le stock total comme base de calcul
      let totalStock = 0;
      let totalValue = 0;
      
      if (products && products.length > 0) {
        products.forEach(product => {
          const stock = parseInt(product.stock) || 0;
          const price = parseFloat(product.price) || 0;
          totalStock += stock;
          totalValue += stock * price;
        });
        console.log(`📦 Total stock: ${totalStock} units, valued at ${totalValue} DA`);
      } else {
        console.warn('⚠️ No product data available for cost calculations');
      }
      
      // Calculer directement le coût d'espace de stockage basé sur les données réelles
      // Valeur estimée d'espace: prix moyen au m² × espace total utilisé
      // Utilisons une approche directe basée uniquement sur la valeur du stock
      let multiplier = 1;
      if (timeRange === 'quarter') multiplier = 3;
      if (timeRange === 'year') multiplier = 12;
      
      // Coût basé uniquement sur la valeur de stock réelle
      const totalCost = Math.round(totalValue * 0.01 * multiplier); // 1% de la valeur du stock par mois
      
      // Utiliser uniquement des données réelles - uniquement le coût d'espace
      const costByCategory = [{
        name: "Espace de stockage",
        value: totalCost,
        percentage: 100
      }];
      
      // 3. Coût par entrepôt - utiliser uniquement les entrepôts réels
      const realWarehouses = warehouses && warehouses.length > 0 
        ? warehouses 
        : [];
      
      const warehouseCosts = realWarehouses.map(warehouse => {
        // Utiliser uniquement des données réelles sur la taille de l'entrepôt
        // La taille de l'entrepôt est stockée au format "1500 m²"
        const capacityMatch = warehouse.capacity ? warehouse.capacity.match(/(\d+)/) : null;
        const squareMeters = capacityMatch ? parseInt(capacityMatch[1]) : 0;
        
        // Utiliser directement itemCount des données de l'entrepôt si disponible
        let itemCount = warehouse.itemCount || 0;
        
        // Si itemCount n'est pas disponible, calculer en fonction des produits
        if (!itemCount && products && products.length > 0) {
          // Chercher les produits associés à cet entrepôt
          // Vérifier si le produit a une référence à cet entrepôt spécifique
          const productsInWarehouse = products.filter(p => 
            (p.warehouseId === warehouse.id) || 
            (p.warehouseIds && p.warehouseIds.includes(warehouse.id))
          );
          
          // Si nous avons des produits associés à cet entrepôt
          if (productsInWarehouse.length > 0) {
            itemCount = productsInWarehouse.reduce((sum, p) => sum + (parseInt(p.stock) || 0), 0);
        } else {
            // Si pas de relation explicite, distribuer équitablement entre entrepôts
            itemCount = Math.round(totalStock / realWarehouses.length);
          }
        }
        
        // Coût proportionnel à la taille de l'entrepôt
        // Calculer la taille totale de tous les entrepôts
        const totalSize = realWarehouses.reduce((sum, w) => {
          const whMatch = w.capacity ? w.capacity.match(/(\d+)/) : null;
          const whSize = whMatch ? parseInt(whMatch[1]) : 500;
          return sum + whSize;
        }, 0)
        
        const warehousePercentage = totalSize > 0 ? squareMeters / totalSize : (1 / realWarehouses.length);
        const cost = Math.round(totalCost * warehousePercentage);
        
        return {
          id: warehouse.id,
          name: warehouse.name,
          cost: cost,
          squareMeters: squareMeters,
          itemCount: itemCount
        };
      });
      
      // Filtrer par entrepôt si spécifié
      const filteredWarehouseCosts = warehouseFilter === 'all'
        ? warehouseCosts
        : warehouseCosts.filter(w => w.id === warehouseFilter);
      
      // 4. Articles les plus coûteux à stocker - utiliser uniquement les produits réels
      const productList = products && products.length > 0 ? products : [];
      
      const topExpensiveItems = productList.map(product => {
        // Utiliser le stock réel comme base pour le calcul
        const stock = parseInt(product.stock) || 1;
        const price = parseFloat(product.price) || 100;
        
        // Calculer les jours de stockage en utilisant l'historique des achats si disponible
        let storageDays = 90; // Valeur par défaut de 90 jours
        
        if (purchases && purchases.length > 0) {
          // Chercher le dernier achat pour ce produit
          const productPurchases = purchases.filter(purchase => {
            if (!purchase.items) return false;
            return purchase.items.some(item => 
              item.productId === product.id || item.id === product.id
            );
          }).sort((a, b) => {
            // Trier par date, du plus récent au plus ancien
            const dateA = new Date(a.date || a.createdAt || Date.now());
            const dateB = new Date(b.date || b.createdAt || Date.now());
            return dateB - dateA;
          });
          
          // Si nous avons des achats pour ce produit, calculer les jours écoulés
          if (productPurchases.length > 0) {
            const lastPurchaseDate = new Date(productPurchases[0].date || productPurchases[0].createdAt || Date.now());
            const today = new Date();
            const diffTime = Math.abs(today - lastPurchaseDate);
            storageDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          }
        }
        
        // Limiter à une période raisonnable (maximum 6 mois)
        storageDays = Math.min(storageDays, 180);
        const quantity = stock;
        
        // Taux journalier basé sur la valeur du produit
        const dailyRate = Math.round(price * 0.0005); // 0.05% de la valeur par jour
        const totalStorageCost = dailyRate * storageDays * quantity;
        
        // Déterminer la catégorie du produit
        const productCategory = product.category || 'Autres';
        const categoryName = productCategories.find(c => c.id === productCategory)?.name || productCategory;
        
        return {
          id: product.id,
          name: product.name,
          category: categoryName,
          storageDays,
          quantity,
          dailyRate,
          totalStorageCost
        };
      })
      // Trier par coût de stockage total (du plus élevé au plus bas)
      .sort((a, b) => b.totalStorageCost - a.totalStorageCost)
      // Limiter aux 10 premiers éléments
      .slice(0, 10);
      
      // 5. Détails des coûts par catégorie de produit
      // Regrouper les produits par catégorie
      const productsByCategory = {};
      productList.forEach(product => {
        const category = product.category || 'Autres';
        if (!productsByCategory[category]) {
          productsByCategory[category] = [];
        }
        productsByCategory[category].push(product);
      });
      
      // Créer des catégories basées sur les produits réels
      const realCategories = Object.keys(productsByCategory).map(catId => {
        const matchingCategory = productCategories.find(c => c.id === catId);
        return {
          id: catId,
          name: matchingCategory ? matchingCategory.name : catId
        };
      });
      
      // Si aucune catégorie réelle n'est trouvée, utiliser les catégories prédéfinies
      const categoriesToUse = realCategories.length > 0 ? realCategories : productCategories;
      
      const costDetailsByCategory = realCategories.map(category => {
        // Calculer la valeur totale des produits dans cette catégorie (donnée réelle)
        const productsInCategory = productsByCategory[category.id] || [];
        const categoryValue = productsInCategory.reduce((sum, product) => {
          const stock = parseInt(product.stock) || 0;
          const price = parseFloat(product.price) || 0;
          return sum + (stock * price);
        }, 0);
        
        // Calculer le pourcentage que cette catégorie représente
        const categoryPercentage = totalValue > 0 ? categoryValue / totalValue : 0;
        
        // Calculer le coût d'espace pour cette catégorie
        const categoryCost = Math.round(totalCost * categoryPercentage);
        
        return {
          name: category.name,
          valeur_stock: categoryValue,
          pourcentage: Math.round(categoryPercentage * 100),
          cout_stockage: categoryCost,
          nombre_produits: productsInCategory.length
        };
      });
      
      setStorageCostsData({
        totalCost,
        costByCategory,
        costByWarehouse: filteredWarehouseCosts,
        topExpensiveItems,
        costDetails: costDetailsByCategory
      });
      
    } catch (error) {
      console.error("Erreur lors du calcul des coûts de stockage:", error);
      toast.error("Erreur lors du calcul des coûts de stockage");
    } finally {
      setIsCalculating(false);
    }
  }, [products, warehouses, stockAdjustments, purchases, warehouseFilter, timeRange, productCategories]);

  // Couleurs pour les graphiques
  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];
  
  // Formater les valeurs monétaires
  const formatMoney = (amount) => {
    // Pour les affichages dans l'interface
    if (typeof document !== 'undefined') {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount).replace('DZD', 'DA');
    } 
    // Pour le PDF (formatage manuel pour éviter les problèmes d'espace)
    else {
      const formattedNumber = Math.round(amount).toLocaleString('fr-FR').replace(/ /g, ' ');
      return `${formattedNumber} DA`;
    }
  };
  
  // PDF Report Generation
  const handleGenerateReport = async () => {
    setIsCalculating(true);
    
    try {
      console.log('🔄 Starting Storage Costs PDF generation...');
      console.log('📊 Data check:', {
        productsCount: products?.length || 0,
        warehousesCount: warehouses?.length || 0,
        costByCategory: storageCostsData?.costByCategory?.length || 0,
        topExpensiveItems: storageCostsData?.topExpensiveItems?.length || 0
      });
      
      // Minimalist red theme colors (consistent with other reports)
      const primaryRed = [185, 28, 28]; // #B91C1C - True red color
      const secondaryRed = [153, 27, 27]; // #991B1B - Darker red
      const lightGray = [248, 248, 248]; // Very light gray for subtle backgrounds
      
      // Create new PDF document
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let currentPage = 1;
      
      // Helper function to add minimal header and footer
      const addHeaderFooter = async () => {
        // HEADER - Company logo and name
        let headerYPosition = 10;
        
        // Add company logo if available
        if (companySettings?.logo) {
          try {
            // Add logo to PDF (small size: 15mm width, proportional height)
            pdf.addImage(companySettings.logo, 'PNG', 20, 5, 15, 10);
            
            // Position company name next to logo
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(0, 0, 0); // Black color
            const companyName = companySettings?.companyName || 'INVENTAIRE V2';
            pdf.text(companyName, 40, 12);
            
            headerYPosition = 18; // Adjust for logo height
          } catch (error) {
            console.log('Error adding logo to PDF:', error);
            // Fallback to text-only header
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(0, 0, 0); // Black color
            const companyName = companySettings?.companyName || 'INVENTAIRE V2';
            pdf.text(companyName, 20, 10);
          }
        } else {
          // No logo - just company name
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(0, 0, 0); // Black color
          const companyName = companySettings?.companyName || 'INVENTAIRE V2';
          pdf.text(companyName, 20, 10);
        }
        
        // Simple thin line below header
        pdf.setDrawColor(...primaryRed);
        pdf.setLineWidth(0.2);
        pdf.line(20, headerYPosition, pageWidth - 20, headerYPosition);
        
        // FOOTER - Software name and page number
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(120, 120, 120);
        pdf.text('INVENTAIRE V2', 20, pageHeight - 8);
        
        // Page number
        pdf.text(`${currentPage}`, pageWidth - 25, pageHeight - 8);
      };
      
      // Helper function to add minimal section header
      const addSectionHeader = async (title, yPos) => {
        // Check if we need a new page
        if (yPos > pageHeight - 40) {
          pdf.addPage();
          currentPage++;
          await addHeaderFooter();
          yPos = 30; // Adjust for new header size
        }
        
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0); // Black color
        pdf.text(title, 20, yPos);
        
        // Thin underline
        pdf.setDrawColor(...primaryRed);
        pdf.setLineWidth(0.1);
        const textWidth = pdf.getTextWidth(title);
        pdf.line(20, yPos + 2, 20 + textWidth, yPos + 2);
        
        return yPos + 8; // Return new position after header
      };
      
      // Start first page
      await addHeaderFooter();
      let yPosition = 30; // Start below larger header with logo
      
      // Main Title
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0); // Black color
      pdf.text('ANALYSE DES COÛTS DE STOCKAGE', 20, yPosition);
      yPosition += 8;
      
      // Date and time
      pdf.setFontSize(9);
      pdf.setTextColor(120, 120, 120);
      pdf.text(`Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}`, 20, yPosition);
      yPosition += 20;
      
      // Filters summary
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(60, 60, 60);
      pdf.text('Filtres appliqués:', 20, yPosition);
      yPosition += 5;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(80, 80, 80);
      
      // Get the filter names for the report
      const timeRangeText = timeRange === 'month' ? 'Ce mois' : 
                          timeRange === 'quarter' ? 'Ce trimestre' : 'Cette année';
      const warehouseText = warehouseFilter === 'all' ? 'Tous les entrepôts' : 
                          warehouseOptions.find(w => w.id === warehouseFilter)?.name || warehouseFilter;
      
      pdf.text(`Période: ${timeRangeText}   |   Entrepôt: ${warehouseText}`, 20, yPosition);
      yPosition += 15;
      
      // Executive Summary
      yPosition = await addSectionHeader('RÉSUMÉ DES COÛTS DE STOCKAGE', yPosition);
      
      const summaryData = [
        ['Coût total de stockage', formatMoney(storageCostsData.totalCost)],
        ['Nombre d\'entrepôts', `${storageCostsData.costByWarehouse.length}`],
        ['Nombre total de produits', `${products?.length || 0}`],
        ['Période', timeRangeText]
      ];
      
      autoTable(pdf, {
        startY: yPosition,
        head: [['Métrique', 'Valeur']],
        body: summaryData,
        theme: 'plain',
        headStyles: { 
          fillColor: primaryRed, 
          textColor: 255, 
          fontSize: 9, 
          halign: 'left' 
        },
        styles: { 
          fontSize: 8, 
          cellPadding: 3, 
          lineColor: [220, 220, 220], 
          lineWidth: 0.1 
        },
        margin: { left: 20, right: 20 }
      });
      
      yPosition = pdf.lastAutoTable.finalY + 15;
      
      // Stock par catégorie (données réelles)
      yPosition = await addSectionHeader('STOCK PAR CATÉGORIE', yPosition);
      
      if (storageCostsData.costDetails && storageCostsData.costDetails.length > 0) {
        // Format cost category data for the table
        const categoryTableData = storageCostsData.costDetails.map(category => [
          category.name,
          category.nombre_produits.toString(),
          formatMoney(category.valeur_stock),
          formatMoney(category.cout_stockage),
          `${category.pourcentage}%`
        ]);
        
        autoTable(pdf, {
          startY: yPosition,
          head: [['Catégorie', 'Nb. Produits', 'Valeur Stock', 'Coût Stockage', 'Pourcentage']],
          body: categoryTableData,
          theme: 'plain',
          headStyles: { 
            fillColor: primaryRed, 
            textColor: 255, 
            fontSize: 8, 
            halign: 'left' 
          },
          styles: { 
            fontSize: 7, 
            cellPadding: 2, 
            lineColor: [230, 230, 230], 
            lineWidth: 0.1 
          },
          alternateRowStyles: { fillColor: lightGray },
          margin: { left: 20, right: 20 }
        });
        
        yPosition = pdf.lastAutoTable.finalY + 15;
      }
      
      // Warehouses
      yPosition = await addSectionHeader('COÛTS PAR ENTREPÔT', yPosition);
      
      if (storageCostsData.costByWarehouse && storageCostsData.costByWarehouse.length > 0) {
        // Format warehouse data for the table
        const warehouseTableData = storageCostsData.costByWarehouse.map(warehouse => [
          warehouse.name,
          `${warehouse.squareMeters} m²`,
          `${warehouse.itemCount}`,
          formatMoney(warehouse.cost)
        ]);
        
        autoTable(pdf, {
          startY: yPosition,
          head: [['Entrepôt', 'Surface', 'Articles', 'Coût']],
          body: warehouseTableData,
          theme: 'plain',
          headStyles: { 
            fillColor: primaryRed, 
            textColor: 255, 
            fontSize: 8, 
            halign: 'left' 
          },
          styles: { 
            fontSize: 7, 
            cellPadding: 2, 
            lineColor: [230, 230, 230], 
            lineWidth: 0.1 
          },
          alternateRowStyles: { fillColor: lightGray },
          margin: { left: 20, right: 20 }
        });
        
        yPosition = pdf.lastAutoTable.finalY + 15;
      }
      
      // Top Expensive Items
      yPosition = await addSectionHeader('PRODUITS LES PLUS COÛTEUX À STOCKER', yPosition);
      
      if (storageCostsData.topExpensiveItems && storageCostsData.topExpensiveItems.length > 0) {
        // Format top items data for the table
        const topItemsTableData = storageCostsData.topExpensiveItems.slice(0, 10).map(item => [
          item.name,
          item.category,
          `${item.quantity}`,
          `${item.storageDays} jours`,
          formatMoney(item.totalStorageCost)
        ]);
        
        autoTable(pdf, {
          startY: yPosition,
          head: [['Produit', 'Catégorie', 'Quantité', 'Durée stockage', 'Coût total']],
          body: topItemsTableData,
          theme: 'plain',
          headStyles: { 
            fillColor: primaryRed, 
            textColor: 255, 
            fontSize: 8, 
            halign: 'left' 
          },
          styles: { 
            fontSize: 7, 
            cellPadding: 2, 
            lineColor: [230, 230, 230], 
            lineWidth: 0.1 
          },
          alternateRowStyles: { fillColor: lightGray },
          margin: { left: 20, right: 20 }
        });
        
        yPosition = pdf.lastAutoTable.finalY + 15;
      }
      
      // Nous utilisons uniquement les données réelles des catégories de produits, déjà affichées dans la section "STOCK PAR CATÉGORIE"
      
      // Recommandations basées sur des données réelles
      yPosition = await addSectionHeader('RECOMMANDATIONS BASÉES SUR LES DONNÉES RÉELLES', yPosition);
      
      // Utiliser une taille de police plus grande pour une meilleure lisibilité
      pdf.setFontSize(9);
      pdf.setTextColor(40, 40, 40);
      
      const hasExpensiveItems = storageCostsData.topExpensiveItems && storageCostsData.topExpensiveItems.length > 0;
      
      // Recommandation basée sur les articles les plus coûteux (données réelles)
      if (hasExpensiveItems) {
        const topItem = storageCostsData.topExpensiveItems[0];
        
        // Titre de la recommandation
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(primaryRed[0], primaryRed[1], primaryRed[2]);
        pdf.text('Produit le plus coûteux à stocker', 20, yPosition);
        yPosition += 6;
        
        // Contenu de la recommandation
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(40, 40, 40);
        const costText = formatMoney(topItem.totalStorageCost);
        pdf.text(`Le produit "${topItem.name}" génère les coûts de stockage les plus élevés (${costText})`, 20, yPosition);
        yPosition += 5;
        pdf.text(`avec une durée de stockage de ${topItem.storageDays} jours.`, 20, yPosition);
        yPosition += 5;
        pdf.text('Il est recommandé d\'optimiser la gestion de ce produit pour réduire les coûts de stockage.', 20, yPosition);
        yPosition += 12;
      }
      
      // Recommandation basée sur la valeur totale du stock (donnée réelle)
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(primaryRed[0], primaryRed[1], primaryRed[2]);
      pdf.text('Valeur du stock', 20, yPosition);
      yPosition += 6;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(40, 40, 40);
      const totalStockValue = products?.reduce((sum, p) => sum + ((parseInt(p.stock) || 0) * (parseFloat(p.price) || 0)), 0) || 0;
      const stockValueText = formatMoney(totalStockValue);
      pdf.text(`La valeur totale de votre stock s'élève à ${stockValueText}.`, 20, yPosition);
      yPosition += 5;
      
      const monthlyCostText = formatMoney(totalStockValue * 0.01);
      pdf.text(`Le coût mensuel de stockage est estimé à ${monthlyCostText} (1% de la valeur du stock).`, 20, yPosition);
      yPosition += 12;
      
      // Recommandation basée sur la répartition par catégorie (données réelles)
      if (storageCostsData.costDetails && storageCostsData.costDetails.length > 0) {
        // Trouver la catégorie avec la plus grande valeur de stock
        const topCategory = [...storageCostsData.costDetails].sort((a, b) => b.valeur_stock - a.valeur_stock)[0];
        
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(primaryRed[0], primaryRed[1], primaryRed[2]);
        pdf.text('Catégorie principale', 20, yPosition);
        yPosition += 6;
        
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(40, 40, 40);
        pdf.text(`La catégorie "${topCategory.name}" représente ${topCategory.pourcentage}% de la valeur totale de votre stock`, 20, yPosition);
        yPosition += 5;
        
        const categoryValueText = formatMoney(topCategory.valeur_stock);
        pdf.text(`avec ${topCategory.nombre_produits} produits pour une valeur de ${categoryValueText}.`, 20, yPosition);
      }
      
      // Add header/footer to all pages
      const totalPages = pdf.getNumberOfPages();
      for (let i = 2; i <= totalPages; i++) {
        pdf.setPage(i);
        currentPage = i;
        await addHeaderFooter();
      }
      
      // Generate filename and download
      const fileName = `Analyse_Couts_Stockage_${new Date().toISOString().slice(0, 10)}.pdf`;
      console.log('💾 Saving PDF:', fileName);
      
      pdf.save(fileName);
      
      setIsCalculating(false);
      toast.success('Rapport PDF généré avec succès!');
      console.log('✅ PDF generation completed successfully');
      
    } catch (error) {
      console.error('❌ PDF generation error:', error);
      setIsCalculating(false);
      toast.error(`Erreur lors de la génération du PDF: ${error.message}`);
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      {/* Header with navigation */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/advanced-analytics')}
            className="p-2 bg-white/10 rounded-xl hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 text-white" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">Analyse des Coûts de Stockage</h1>
            <p className="text-white/60 text-sm mt-1">
              Évaluation des coûts de possession et optimisation des ressources
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={handleGenerateReport} 
            disabled={isCalculating}
            className="flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl border border-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <DocumentArrowDownIcon className="h-5 w-5" />
            <span>{isCalculating ? 'Génération...' : 'Exporter PDF'}</span>
          </button>
        </div>
      </div>
      
      {/* Filters */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex items-center space-x-3">
            <FunnelIcon className="h-5 w-5 text-white/70" />
            <h3 className="text-lg font-medium text-white">Filtres</h3>
          </div>
          
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-white/70">Entrepôt:</span>
              <select
                value={warehouseFilter}
                onChange={(e) => setWarehouseFilter(e.target.value)}
                className="bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {warehouseOptions.map(warehouse => (
                  <option key={warehouse.id} value={warehouse.id}>
                    {warehouse.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-sm text-white/70">Période:</span>
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {timeRanges.map(range => (
                  <option key={range.id} value={range.id}>
                    {range.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main stats card - Données réelles uniquement */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-white text-xl font-semibold flex items-center">
                  <BuildingLibraryIcon className="h-6 w-6 mr-2" />
                  Coût total de stockage
                </h3>
                <p className="text-white/60 text-sm mt-1">
                  {timeRange === 'month' ? 'Mensuel' : 
                   timeRange === 'quarter' ? 'Trimestriel' : 'Annuel'} pour {warehouseFilter === 'all' ? 'tous les entrepôts' : warehouseOptions.find(w => w.id === warehouseFilter)?.name}
                </p>
            <p className="text-white/60 text-sm mt-1">
              Basé sur la valeur réelle du stock: 1% par mois
            </p>
              </div>
              <div className="text-4xl font-bold text-white">
                {formatMoney(storageCostsData.totalCost)}
              </div>
            </div>
            
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-white text-lg font-medium mb-4">Données réelles du stock</h4>
            <div className="space-y-3">
              <div className="bg-white/5 rounded-xl p-4">
                    <div className="flex justify-between items-center mb-1">
                  <span className="text-white font-medium">Valeur totale du stock</span>
                  <span className="text-white">{formatMoney(products?.reduce((sum, p) => sum + ((parseInt(p.stock) || 0) * (parseFloat(p.price) || 0)), 0) || 0)}</span>
                </div>
                    </div>
              
              <div className="bg-white/5 rounded-xl p-4">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-white font-medium">Nombre d'entrepôts</span>
                  <span className="text-white">{warehouses?.length || 0}</span>
                    </div>
                  </div>
              
              <div className="bg-white/5 rounded-xl p-4">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-white font-medium">Nombre total de produits</span>
                  <span className="text-white">{products?.length || 0}</span>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="text-white text-lg font-medium mb-4">Stock par entrepôt</h4>
            <div className="space-y-3">
              {storageCostsData.costByWarehouse.map((warehouse) => (
                <div key={warehouse.id} className="bg-white/5 rounded-xl p-4">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-white font-medium">{warehouse.name}</span>
                    <span className="text-white">{formatMoney(warehouse.cost)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-white/70">
                    <span>{warehouse.squareMeters} m²</span>
                    <span>{warehouse.itemCount} articles</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Catégories de produits - données réelles */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <h3 className="text-xl font-semibold text-white mb-4">Stock par catégorie de produit</h3>
        <p className="text-white/60 text-sm mb-6">
          Données réelles basées sur les produits actuellement en stock
        </p>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/5">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Catégorie</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Nb. Produits</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Valeur Stock</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Coût Stockage</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {storageCostsData.costDetails && storageCostsData.costDetails.map((category, index) => (
                <tr key={index} className="hover:bg-white/5">
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-white">{category.name}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-white text-right">{category.nombre_produits}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-white text-right">{formatMoney(category.valeur_stock)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-white text-right">{formatMoney(category.cout_stockage)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
      </div>
      
      {/* Graphique basé sur les données réelles */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <h3 className="text-xl font-semibold text-white mb-6">Valeur de stock par catégorie</h3>
        
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={storageCostsData.costDetails}
              margin={{ top: 20, right: 30, left: 30, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis 
                dataKey="name" 
                stroke="rgba(255,255,255,0.6)"
              />
              <YAxis stroke="rgba(255,255,255,0.6)" />
              <Tooltip 
                formatter={(value) => formatMoney(value)}
                contentStyle={{ 
                  backgroundColor: 'rgba(17, 24, 39, 0.9)',
                  borderColor: 'rgba(255,255,255,0.2)',
                  color: '#fff'
                }}
              />
              <Legend 
                formatter={(value, entry, index) => (
                  <span style={{ color: '#ffffff' }}>{value}</span>
                )}
              />
              <Bar dataKey="valeur_stock" name="Valeur du Stock" fill="#3B82F6" />
              <Bar dataKey="cout_stockage" name="Coût de Stockage" fill="#10B981" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Most expensive items to store */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden">
        <div className="p-6 border-b border-white/10">
          <h3 className="text-xl font-semibold text-white flex items-center">
            <CurrencyDollarIcon className="h-6 w-6 mr-2" />
            Articles les plus coûteux à stocker
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-white/10">
            <thead className="bg-white/5">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Article</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Catégorie</th>
                <th className="px-6 py-4 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Quantité</th>
                <th className="px-6 py-4 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Jours de stockage</th>
                <th className="px-6 py-4 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Coût journalier</th>
                <th className="px-6 py-4 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Coût total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {storageCostsData.topExpensiveItems.map((item, index) => (
                <tr key={item.id} className="hover:bg-white/5 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{item.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white/80">{item.category}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-white/80">{item.quantity}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-white/80">{item.storageDays}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-white/80">{formatMoney(item.dailyRate)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-white">{formatMoney(item.totalStorageCost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Optimization tips */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <h3 className="text-xl font-semibold text-white mb-4">Conseils d'optimisation</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white/5 rounded-xl p-5 flex space-x-4">
            <div className="p-3 bg-amber-500/20 rounded-lg h-fit">
              <ClockIcon className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <h4 className="text-white font-medium">Réduire le temps de stockage</h4>
              <p className="text-white/70 text-sm mt-1">
                Adoptez une approche Just-in-Time pour réduire la durée de stockage des articles à forte valeur.
              </p>
            </div>
          </div>
          
          <div className="bg-white/5 rounded-xl p-5 flex space-x-4">
            <div className="p-3 bg-green-500/20 rounded-lg h-fit">
              <CurrencyDollarIcon className="h-5 w-5 text-green-400" />
            </div>
            <div>
              <h4 className="text-white font-medium">Analyser les coûts cachés</h4>
              <p className="text-white/70 text-sm mt-1">
                Les coûts d'opportunité représentent 10% du total. Optimisez votre capital de travail en réduisant les stocks dormants.
              </p>
            </div>
          </div>
          
          <div className="bg-white/5 rounded-xl p-5 flex space-x-4">
            <div className="p-3 bg-blue-500/20 rounded-lg h-fit">
              <ShieldExclamationIcon className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <h4 className="text-white font-medium">Réviser les polices d'assurance</h4>
              <p className="text-white/70 text-sm mt-1">
                Négociez les primes d'assurance en fonction des niveaux de risque réels et regroupez les polices pour économiser.
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default StorageCostsAnalysis; 